import { useI18n } from "@/context/I18nContext";

const Strip = ({ id, w, h = 34, children, testid }) => (
  <svg className="absolute inset-x-0 top-0 w-full" height={h} aria-hidden="true" data-testid={testid}>
    <defs><pattern id={id} width={w} height={h} patternUnits="userSpaceOnUse">{children}</pattern></defs>
    <rect width="100%" height={h} fill={`url(#${id})`} />
  </svg>
);

const Lights = () => {
  const cols = ["#ff4d6d", "#2ee59d", "#ffd34d", "#4db8ff"];
  return (
    <Strip id="hol-lights" w={144} testid="hol-top-lights">
      <path d="M0 3 Q18 13 36 3 T72 3 T108 3 T144 3" fill="none" stroke="#2a3a2a" strokeWidth="1.6" />
      {cols.map((c, i) => (
        <g key={c} className="cx-hol-twinkle" style={{ animationDelay: `${i * 0.45}s`, color: c }}>
          <rect x={18 + i * 36 - 2} y={7} width={4} height={4} rx={1} fill="#3a3a3a" />
          <ellipse cx={18 + i * 36} cy={16} rx={4} ry={6} fill={c} />
        </g>
      ))}
    </Strip>
  );
};

const MARKS = {
  dot: (x) => <circle cx={x} cy={12} r={2.4} fill="rgba(255,255,255,.85)" />,
  leaf: (x, i) => i % 2 === 1 && <path d={`M${x} 7 l2 4 3-1 -1 4 2 1 -4 2 0 3 -2-1 -2 1 0-3 -4-2 2-1 -1-4 3 1z`} fill="#ff3346" />,
  clover: (x, i) => i % 2 === 0 && <g fill="rgba(255,255,255,.6)">{[[-2.5, -1.5], [2.5, -1.5], [0, 2]].map(([dx, dy]) => <circle key={`${dx}${dy}`} cx={x + dx} cy={12 + dy} r={2.3} />)}</g>,
  lys: (x, i) => i % 2 === 1 && <path d={`M${x} 7 c2 3 2 6 0 9 c-2-3 -2-6 0-9z M${x - 5} 11 c3 0 4 3 5 5 c-3 0 -5-2 -5-5z M${x + 5} 11 c-3 0 -4 3 -5 5 c3 0 5-2 5-5z`} fill="#1f5fd1" />,
};

const Bunting = ({ id, cols, mark }) => (
  <Strip id={id} w={cols.length * 36} h={22} testid="hol-top-bunting">
    <path d={`M0 3 ${cols.map((_, i) => `Q${i * 36 + 18} 7 ${i * 36 + 36} 3`).join(" ")}`} fill="none" stroke="rgba(255,255,255,.55)" strokeWidth="1.2" />
    {cols.map((c, i) => (
      <g key={i}>
        <polygon points={`${i * 36 + 6},4 ${i * 36 + 30},4 ${i * 36 + 18},19`} fill={c} opacity=".92" />
        <g transform={`translate(${i * 36 + 18} 9.5) scale(.62) translate(${-(i * 36 + 18)} -12)`}>{MARKS[mark](i * 36 + 18, i)}</g>
      </g>
    ))}
  </Strip>
);

const Hearts = () => (
  <Strip id="hol-hearts" w={84} h={36} testid="hol-top-hearts">
    <path d="M0 2 Q21 7 42 2 T84 2" fill="none" stroke="rgba(255,120,170,.6)" strokeWidth="1.2" />
    {[[21, 13, "#ff5fa2"], [63, 18, "#ff2e63"]].map(([x, y, c], i) => (
      <g key={x} className="cx-hol-twinkle" style={{ color: c, animationDelay: `${i * 0.9}s` }}>
        <line x1={x} y1={5} x2={x} y2={y - 5} stroke="rgba(255,150,190,.5)" strokeWidth=".8" />
        <path d={`M${x} ${y + 4} l-5-5 a3 3 0 0 1 5-4 a3 3 0 0 1 5 4z`} fill={c} />
      </g>
    ))}
  </Strip>
);

const Streamers = () => (
  <Strip id="hol-streamers" w={96} h={34} testid="hol-top-streamers">
    <path d="M0 2 H96" stroke="rgba(255,211,77,.5)" strokeWidth="1.2" />
    <path d="M24 2 q4 3 0 6 q-4 3 0 6 q4 3 0 6" fill="none" stroke="#ffd34d" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M72 2 q-4 3 0 6 q4 3 0 6" fill="none" stroke="#d9dcff" strokeWidth="1.8" strokeLinecap="round" />
    <path className="cx-hol-twinkle" style={{ color: "#fff3b0" }} d="M48 6 l1.4 3 3 1.4 -3 1.4 -1.4 3 -1.4-3 -3-1.4 3-1.4z" fill="#fff3b0" />
  </Strip>
);

const ANG = [0, 18, 36, 54, 72, 90].map((a) => (a * Math.PI) / 180);
const ring = (r) => ANG.map((a, i) => `${i ? `Q${(r * 0.82 * Math.cos(a - 0.157)).toFixed(1)} ${(r * 0.82 * Math.sin(a - 0.157)).toFixed(1)} ` : "M"}${(r * Math.cos(a)).toFixed(1)} ${(r * Math.sin(a)).toFixed(1)}`).join(" ");

const Web = ({ flip }) => (
  <svg width="92" height="92" viewBox="0 0 92 92" className={`absolute top-0 ${flip ? "right-0 -scale-x-100" : "left-0"}`} aria-hidden="true" data-testid={`hol-top-web-${flip ? "r" : "l"}`}>
    <g fill="none" stroke="rgba(230,230,255,.38)" strokeWidth=".9">
      {ANG.map((a) => <line key={a} x1="0" y1="0" x2={(92 * Math.cos(a)).toFixed(1)} y2={(92 * Math.sin(a)).toFixed(1)} />)}
      {[22, 42, 62, 82].map((r) => <path key={r} d={ring(r)} />)}
    </g>
  </svg>
);

const Spider = () => (
  <div className="absolute top-0 left-[230px] cx-hol-spider" aria-hidden="true" data-testid="hol-top-spider">
    <div className="mx-auto w-px h-full bg-[rgba(230,230,255,.35)]" />
    <svg width="18" height="16" viewBox="0 0 18 16" className="absolute -bottom-3 left-1/2 -translate-x-1/2">
      <g stroke="#1a1020" strokeWidth="1.2" fill="none">{[3, 6, 9].map((y) => <path key={y} d={`M9 ${y + 2} L2 ${y} M9 ${y + 2} L16 ${y}`} />)}</g>
      <ellipse cx="9" cy="9" rx="4" ry="5" fill="#1a1020" stroke="#ff8a1f" strokeWidth=".6" />
      <circle cx="7.6" cy="7.5" r=".8" fill="#ff8a1f" /><circle cx="10.4" cy="7.5" r=".8" fill="#ff8a1f" />
    </svg>
  </div>
);

const SaleTag = () => {
  const { lang } = useI18n();
  return (
    <>
      <div className="absolute inset-x-0 top-0 h-[3px] opacity-70" style={{ background: "repeating-linear-gradient(135deg,#ffd400 0 8px,#111 8px 16px)" }} />
      <div className="absolute top-0 left-[230px] cx-hol-swing" data-testid="hol-top-tag">
        <div className="mx-auto w-px h-4 bg-white/40" />
        <span className="block rounded-md border border-[#ff3b3b] bg-black/85 px-1.5 py-0.5 text-[9px] font-black tracking-wider text-[#ffd400] shadow-[0_0_10px_rgba(255,59,59,.5)]">{lang === "fr" ? "SOLDE %" : "SALE %"}</span>
      </div>
    </>
  );
};

const DECORS = {
  noel: () => <Lights />,
  halloween: () => <><Web /><Web flip /><Spider /></>,
  paques: () => <Bunting id="hol-easter" cols={["#ffb3d1", "#fff0a0", "#a8f0d0", "#cdb8ff"]} mark="dot" />,
  valentin: () => <Hearts />,
  fetenat: () => <Bunting id="hol-qc" cols={["#1f5fd1", "#ffffff"]} mark="lys" />,
  nouvelan: () => <Streamers />,
  blackfriday: () => <SaleTag />,
  stpatrick: () => <Bunting id="hol-irish" cols={["#1fbf4a", "#ffd166", "#7dffa0"]} mark="clover" />,
  canada: () => <Bunting id="hol-canada" cols={["#e8202f", "#ffffff"]} mark="leaf" />,
};

export const HolidayTopDecor = ({ theme }) => {
  const D = DECORS[theme.id];
  if (!D) return null;
  return <div className="cx-hol-top" data-testid={`holiday-top-decor-${theme.id}`}><D /></div>;
};
